<template>
  <TransitionRoot as="template" :show="precheckOpen">
    <Dialog as="div" class="fixed z-10 zoom-80 inset-0 overflow-y-auto" @close="closeModal">
      <div
        class="flex items-end justify-center min-h-screen px-4 pb-20 text-center sm:block sm:p-0"
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="
              inline-block
              align-bottom
              bg-white
              rounded-3xl
              md:absolute
              md:top-1/2
              md:left-1/2
              transform
              md:-translate-x-1/2 md:-translate-y-1/2
              text-left
              overflow-hidden
              shadow-xl
              transition-all
              w-full
              sm:max-w-4xl
              sm:w-full
            "
          >
            <!-- Header -->
            <div class="p-5 2xl:py-5 sm:px-6 border-gray-200 rounded-t-lg border bg-gray-50">
              <div class="inline-flex w-full justify-between">
                <h2 class="mb-1 2xl:mb-4 text-xl leading-7 font-semibold text-vice-teal-900">
                  Before we start...
                </h2>
                <CrossGrayIcon class="h-7 w-7 cursor-pointer" @click="closeModal" />
              </div>
              <div class="border-gray-150 text-gray-500">
                <ul class="list-disc mx-5 text-xs">
                  <li>
                    <span>Allow browser access to your camera and microphone.</span>
                  </li>
                  <li>
                    <span>Close other apps or tabs using your camera or mic.</span>
                  </li>
                  <li>
                    <span>Stay plugged in if you're on a laptop.</span>
                  </li>
                </ul>
              </div>
            </div>

            <!-- Body -->
            <div class="p-5 2xl:py-5 sm:px-6">
              <!-- Main devices section -->
              <div class="w-full justify-content-center my-2">
                <CameraAndMicroPrecheck
                  v-if="permissionsGranted"
                  v-bind:devices="devices"
                  :useCases="this.$page.props.deviceTypesUseCases"
                  :isClinician="this.$page.props.isClinician"
                  :highlighted="highlighted"
                  :sessionId="session?.id"
                  :proceed="proceed"
                  @echoWarning="echoWarning = $event"
                  @precheckPassed="isPrecheckPassed = $event"
                  @deviceSetupMatched="deviceSetupMatchEvent"
                />
                <PermissionsOverlay v-else :permissionsStatus="permissionsStatus" />
              </div>

              <!-- Medical devices -->
              <div
                role="list"
                v-if="this.$page.props.isClinician"
                class="border mt-4 w-full pl-3 rounded-lg bg-vice-easy-grey"
              >
                <div
                  class="
                    w-full
                    inline-flex
                    font-medium
                    text-md text-gray-900
                    justify-between
                    leading-7
                    gap-3
                    pl-1
                    pr-4
                    py-2
                    cursor-pointer
                    select-none
                  "
                  @click="isDevicesListOpen = !isDevicesListOpen"
                >
                  <div class="inline-flex gap-3">
                    <!-- Devices check status icon -->
                    <div class="flex">
                      <span v-if="missingDevicesCount === 0" class="pt-1">
                        <CheckIcon class="transition text-white rounded-full bg-vice-teal-500" />
                      </span>
                      <span v-else class="pt-1">
                        <CheckIcon class="transition text-white rounded-full bg-vice-orange" />
                      </span>
                    </div>
                    <span>
                      Devices - {{ connectedDevicesCount }}/{{ clinicianMedicalDevices.length }}
                      connected
                    </span>
                    <!-- <span v-if="missingDevicesCount > 0">({{ missingDevicesCount }} missing)</span> -->
                  </div>

                  <!-- Dropdown arrow -->
                  <ChevronDownIcon
                    class="transition-transform duration-300 w-5 h-5 self-center text-gray-500"
                    :class="{ 'rotate-180': isDevicesListOpen }"
                  />
                </div>

                <Transition
                  enter-active-class="transition-all duration-300 ease-out overflow-hidden"
                  leave-active-class="transition-all duration-300 ease-in overflow-hidden"
                  enter-from-class="max-h-0 opacity-0"
                  enter-to-class="max-h-screen opacity-100"
                  leave-from-class="max-h-screen opacity-100"
                  leave-to-class="max-h-0 opacity-0"
                >
                  <div v-show="isDevicesListOpen" class="pb-3 pr-3">
                    <div
                      v-if="clinicianMedicalDevices.length === 0"
                      class="pl-1 py-2 text-sm text-gray-400"
                    >
                      No medical devices configured for this session
                    </div>
                    <div class="flex gap-2 justify-start">
                      <div
                        v-for="device in clinicianMedicalDevices"
                        :key="device.id"
                        class="relative border w-1/4 rounded-xl p-3 bg-white flex flex-col gap-3"
                        :class="
                          isDeviceConnected(device)
                            ? 'border-gray-300'
                            : device.device_data?.label
                            ? 'border-yellow-300'
                            : 'border-gray-200'
                        "
                      >
                        <!-- Device tile topline -->
                        <div class="flex items-center justify-between">
                          <!-- Icon -->
                          <div
                            class="flex rounded-full w-10 h-10 shrink-0 justify-center items-center"
                            :class="
                              isDeviceConnected(device) ? 'bg-vice-teal-500' : 'bg-vice-orange'
                            "
                          >
                            <img
                              v-if="this.$page.props.deviceTypes[device.device_type_label] || null"
                              :src="
                                '/storage/' + this.$page.props.deviceTypes[device.device_type_label]
                              "
                              :alt="device.device_data?.label"
                              width="20"
                              height="20"
                              class=""
                            />
                          </div>
                          <!-- Status badge -->
                          <span
                            class="self-start text-xxs font-medium px-2 py-0 rounded-full"
                            :class="
                              isDeviceConnected(device)
                                ? 'bg-vice-teal-500 text-white'
                                : device.device_data?.label
                                ? 'bg-yellow text-yellow-700'
                                : 'bg-gray-100 text-gray-500'
                            "
                          >
                            {{
                              isDeviceConnected(device)
                                ? 'Connected'
                                : device.device_data?.label
                                ? 'Not connected'
                                : 'Not configured'
                            }}
                          </span>
                        </div>
                        <!-- Labels -->
                        <div class="flex-1">
                          <div
                            class="font-medium leading-tight"
                            style="color: #881337; font-size: 14px"
                          >
                            {{ device.device_type_label }}
                          </div>
                          <div class="mt-0.5 leading-tight" style="font-size: 11px; color: #4b4b4b">
                            <span v-if="device.device_data?.label">
                              {{ device.device_data.label }}
                            </span>
                            <span v-else class="text-gray-400">Not configured</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Transition>
              </div>

              <!-- System checks -->
              <div role="list" class="border mt-4 w-full pl-3 rounded-lg bg-vice-easy-grey">
                <div
                  class="
                    w-full
                    inline-flex
                    font-medium
                    text-md text-gray-900
                    justify-between
                    leading-7
                    gap-3
                    pl-1
                    pr-4
                    py-2
                    cursor-pointer
                    select-none
                  "
                  @click="isChecklistOpen = !isChecklistOpen"
                >
                  <div class="inline-flex gap-3">
                    <!-- Check status icon -->
                    <div class="flex">
                      <span v-if="true" class="pt-1">
                        <CheckIcon
                          style="background-color: rgb(62 183 166)"
                          class="transition text-white rounded-full"
                        />
                      </span>
                      <span v-else class="pt-1">
                        <CheckIcon class="transition text-white rounded-full bg-vice-yellow" />
                      </span>
                    </div>
                    <span>System checks</span>
                  </div>

                  <!-- Dropdown arrow -->
                  <ChevronDownIcon
                    class="transition-transform duration-300 w-5 h-5 self-center text-gray-500"
                    :class="{ 'rotate-180': isChecklistOpen }"
                  />
                </div>

                <Transition
                  enter-active-class="transition-all duration-300 ease-out overflow-hidden"
                  leave-active-class="transition-all duration-300 ease-in overflow-hidden"
                  enter-from-class="max-h-0 opacity-0"
                  enter-to-class="max-h-screen opacity-100"
                  leave-from-class="max-h-screen opacity-100"
                  leave-to-class="max-h-0 opacity-0"
                >
                  <div v-show="isChecklistOpen">
                    <ConnectionPrecheck :isClinician="this.$page.props.isClinician" />
                  </div>
                </Transition>
              </div>

              <!-- Echo warning -->
              <div
                v-if="echoWarning"
                class="
                  justify-between
                  mt-4
                  mb-0
                  flex
                  gap-3
                  px-4
                  py-2
                  text-sm
                  rounded-lg
                  leading-7
                  font-medium
                  items-center
                  text-vice-teal-900
                  bg-vice-light-beige
                  border border-vice-yellow
                "
              >
                <div class="flex gap-3 items-center">
                  <InfoIcon class="flex w-5 h-5" fill="#ffcd01" />
                  <span class="flex font-semibold">
                    Your mic and speaker are on different devices, this can cause echo.
                  </span>
                </div>
                <ViceButton
                  @click="highlightDevicesInput"
                  class="cursor-pointer px-7 w-1/6"
                  :loading="false"
                  orange
                  small
                >
                  <span class="font-semibold">Change</span>
                </ViceButton>
              </div>

              <!-- Device setup warning -->
              <div
                v-if="!deviceSetupMatched.status"
                class="
                  justify-between
                  mt-4
                  mb-0
                  flex
                  gap-3
                  px-4
                  py-2
                  text-sm
                  leading-7
                  rounded-lg
                  font-medium
                  items-center
                  text-vice-teal-900
                  bg-vice-light-beige
                  border border-vice-yellow
                "
              >
                <div class="flex gap-3 items-center">
                  <InfoIcon class="w-5 h-5 flex" fill="#ffcd01" />
                  <div class="flex-col leading-5">
                    <span class="flex font-semibold">
                      Your selected devices differ from your saved preferences.
                    </span>
                    <ul
                      v-for="(match, index) in deviceSetupMatched.matches"
                      :key="index"
                      class="list-disc ml-5"
                    >
                      <li>
                        You selected
                        <span class="font-bold">{{ match.new }},</span>
                        but your saved preference is
                        <span class="font-bold">{{ match.old }}.</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <ViceButton
                  @click="highlightDevicesInput"
                  class="cursor-pointer px-7 w-1/6"
                  :loading="false"
                  orange
                  small
                >
                  <span class="font-semibold">Change</span>
                </ViceButton>
              </div>

              <!-- Support link -->
              <div v-if="!this.$page.props.isClinician" class="flex justify-center">
                <span class="text-md font-medium text-gray-900 mt-4">
                  If you have any issues, please contact
                  <span class="underline cursor-pointer" @click="openIntercom">support</span>
                </span>
              </div>

              <!-- Buttons -->
              <div class="flex justify-center gap-4 pb-1 mt-6 border-gray-200">
                <ViceButton
                  v-if="!deviceSetupMatched.status"
                  ref="saveAndContinueWizardRef"
                  data-cy="entry-save-start-session"
                  @click="openSessionEntryWizard"
                  class="cursor-pointer step-confirm-start-session px-7"
                  :class="[
                    {
                      'pointer-events-none opacity-60 sm:bg-gray-150 sm:text-black':
                        !isPrecheckPassed,
                    },
                  ]"
                  :loading="false"
                  red
                >
                  Use for this session
                </ViceButton>
                <!-- Continue button for clinicians -->
                <ViceButton
                  v-else
                  ref="continueWizardRef"
                  data-cy="entry-start-session"
                  @click="openSessionEntryWizard"
                  class="cursor-pointer step-confirm-start-session px-7"
                  :class="[
                    {
                      'pointer-events-none opacity-60 sm:bg-gray-150 sm:text-black':
                        !isPrecheckPassed,
                    },
                  ]"
                  :loading="false"
                  red
                >
                  Continue
                </ViceButton>
                <inertia-link
                  v-if="!deviceSetupMatched.status"
                  :href="route('clinician.device-session-setup')"
                  class="
                    px-4
                    leading-7
                    text-sm
                    rounded-md
                    font-semibold
                    content-center
                    hover:bg-vice-light-grey
                  "
                >
                  Device set-up ⟶
                </inertia-link>
              </div>
              <div v-if="!this.$page.props.isClinician" class="flex mt-4 border-t">
                <p class="text-lg leading-10 font-bold text-vice-teal-900">
                  <span
                    class="
                      inline-block
                      h-7
                      w-7
                      mr-5
                      rounded-full
                      text-sm text-center
                      leading-7
                      bg-gray-500
                      text-white
                    "
                  >
                    2
                  </span>
                  Review professional standards
                </p>
              </div>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { DialogOverlay, Dialog, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { onUnmounted, ref, computed } from 'vue';
import { show } from '@intercom/messenger-js-sdk';
import { ChevronDownIcon } from '@heroicons/vue/solid';
import ViceButton from '@/Components/Buttons/ViceButton.vue';
import CrossGrayIcon from '@/Components/Icons/CrossGrayIcon.vue';
import CameraAndMicroPrecheck from '@/Components/Devices/CameraAndMicroPrecheck.vue';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import PermissionsOverlay from '@/Components/Devices/PermissionsOverlay.vue';
import InfoIcon from '@/Components/Icons/InfoIcon.vue';
import ConnectionPrecheck from '@/Components/Devices/ConnectionPrecheck.vue';
import CheckIcon from '@/Components/Icons/CheckIcon.vue';

export default {
  name: 'SessionEntryPrecheckWizard',
  props: {
    precheckOpen: {
      type: Boolean,
      required: true,
    },
    devices: {
      type: Object,
    },
    session: {
      type: Object,
    },
  },
  components: {
    CheckIcon,
    ChevronDownIcon,
    ConnectionPrecheck,
    InfoIcon,
    CrossGrayIcon,
    CameraAndMicroPrecheck,
    Dialog,
    ViceButton,
    DialogOverlay,
    TransitionRoot,
    TransitionChild,
    PermissionsOverlay,
  },
  emits: ['closeSessionEntryPrecheckWizard', 'openSessionEntryWizard'],
  setup(props, { emit }) {
    const proceed = ref(false);
    const echoWarning = ref(false);
    const highlighted = ref(false);
    const isChecklistOpen = ref(false);
    const isDevicesListOpen = ref(false);
    const isPrecheckPassed = ref(false);
    const deviceSetupMatched = ref({ status: true });
    const permissionsGranted = ref(false);
    const permissionsStatus = ref({ camera: null, microphone: null });

    const multiCameraDevicesStore = useLocalStreamingDevicesStore();

    const connectedBrowserDevices = ref([]);

    const refreshBrowserDevices = async () => {
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        connectedBrowserDevices.value = all.filter(
          (d) => d.deviceId !== 'default' && d.deviceId !== 'communications' && d.label,
        );
      } catch {
        // ignore — permissions not yet granted
      }
    };

    navigator.mediaDevices.addEventListener('devicechange', refreshBrowserDevices);

    const clinicianMedicalDevices = computed(() =>
      (props.session?.clinician_devices ?? []).filter(
        (device) => device.device_type_label !== 'Main Camera',
      ),
    );

    const isDeviceConnected = (device) => {
      const label = device.device_data?.label;
      if (!label) return false;
      return connectedBrowserDevices.value.some((d) => d.label === label);
    };

    const connectedDevicesCount = computed(
      () => clinicianMedicalDevices.value.filter((device) => isDeviceConnected(device)).length,
    );

    const allDevicesConnected = computed(
      () =>
        clinicianMedicalDevices.value.length > 0 &&
        connectedDevicesCount.value === clinicianMedicalDevices.value.length,
    );

    const missingDevicesCount = computed(
      () => clinicianMedicalDevices.value.length - connectedDevicesCount.value,
    );

    const checkAndUpdatePermissions = async () => {
      try {
        const permissions = await multiCameraDevicesStore.checkPermissions();
        permissionsStatus.value = permissions;
        permissionsGranted.value = !Object.values(permissions).includes(false);
        if (!permissionsGranted.value) {
          isPrecheckPassed.value = false;
        } else {
          await refreshBrowserDevices();
        }
      } catch (error) {
        console.error('Error checking permissions:', error);
        permissionsGranted.value = false;
        isPrecheckPassed.value = false;
      }
    };

    const permissionCheckInterval = setInterval(checkAndUpdatePermissions, 2000);

    onUnmounted(() => {
      clearInterval(permissionCheckInterval);
      navigator.mediaDevices.removeEventListener('devicechange', refreshBrowserDevices);
    });

    const closeModal = () => {
      echoWarning.value = false;
      isPrecheckPassed.value = false;
      deviceSetupMatched.value.status = true;

      emit('closeSessionEntryPrecheckWizard');
    };

    const openSessionEntryWizard = () => {
      setTimeout(() => {
        proceed.value = true;
      }, 100);

      emit('openSessionEntryWizard');
      closeModal();
    };

    const highlightDevicesInput = () => {
      highlighted.value = true;
      setTimeout(() => {
        highlighted.value = false;
      }, 1000);
    };

    const deviceSetupMatchEvent = (event) => {
      deviceSetupMatched.value = event;
      setTimeout(() => {
        highlighted.value = false;
      }, 1000);
    };

    const openIntercom = () => show();

    return {
      proceed,
      highlighted,
      echoWarning,
      isChecklistOpen,
      isDevicesListOpen,
      isPrecheckPassed,
      permissionsStatus,
      permissionsGranted,
      deviceSetupMatched,
      clinicianMedicalDevices,
      isDeviceConnected,
      connectedDevicesCount,
      allDevicesConnected,
      missingDevicesCount,
      closeModal,
      openIntercom,
      deviceSetupMatchEvent,
      highlightDevicesInput,
      openSessionEntryWizard,
    };
  },
};
</script>

<style scoped>
.arrow-down {
  font-family: 'Segoe UI Symbol', serif;
  font-size: 1rem;
}
</style>
